import React, { useMemo, useState, useEffect } from 'react';
import EtcPopover from './EtcPopover';

/**
 * Dedicated context for the currently-open popover breadcrumb.
 * Kept separate from the main row context so flipping it doesn't
 * invalidate the heavy row context value (which would force every
 * ThirdPartyRow consumer to re-render and delay paint).
 */
export const PopoverBreadcrumbContext = React.createContext(null);

// Walk an objectMap (tree) and find the node whose data.breadcrumb === target.
// Returns the node ({ rows, data }) or null.
const findNodeByBreadcrumb = (objectMap, target) => {
    if (!objectMap || !target) return null;
    const stack = Object.values(objectMap);
    while (stack.length) {
        const node = stack.shift();
        if (!node) continue;
        if (node?.data?.breadcrumb === target) return node;
        if (node?.rows && node.rows !== false) {
            stack.push(...Object.values(node.rows));
        }
    }
    return null;
};

// Collect every entry below a node. Uses a prefix scan over
// entriesByBreadcrumb instead of walking node.rows — that way entries saved at
// breadcrumbs the tree didn't materialize (e.g. deeper-than-rendered paths
// created by max-depth resolveDepth) are still surfaced in the popover.
const collectChildEntries = (parentBreadcrumb, entriesByBreadcrumb) => {
    if (!parentBreadcrumb) return [];
    const prefix = parentBreadcrumb + '^';
    const out = [];
    Object.entries(entriesByBreadcrumb).forEach(([bc, entries]) => {
        if (bc !== parentBreadcrumb && bc.startsWith(prefix)) {
            out.push(...entries);
        }
    });
    return out;
};

/**
 * EtcPopoverHost — Single floating modal that renders whenever popoverBreadcrumb
 * is set. Looks up the matching tree node + entries and passes them to EtcPopover.
 *
 * Props:
 *   objectMap             — top-level tree (pivot render root)
 *   popoverBreadcrumb     — the currently-open breadcrumb (or null)
 *   setPopoverBreadcrumb  — setter to close the popover
 *   entriesByBreadcrumb   — map of breadcrumb → entries[]
 *   pivotPopoverProps     — shared props bundle (drillColumns, tabConfig, etc.)
 *   topLevelNodeData      — fallback nodeData for the 't' (new-at-top) breadcrumb
 */
const EtcPopoverHost = ({
    objectMap,
    popoverBreadcrumb,
    setPopoverBreadcrumb,
    entriesByBreadcrumb = {},
    pivotPopoverProps,
    topLevelNodeData,
}) => {
    // Two-phase mount: when popoverBreadcrumb flips on, paint a lightweight
    // backdrop+spinner first so the user sees instant click feedback, then
    // mount the heavy EtcPopover on the next tick. Without this, React batches
    // both into one paint and the click feels unresponsive.
    const [readyToRender, setReadyToRender] = useState(false);
    useEffect(() => {
        if (!popoverBreadcrumb) { setReadyToRender(false); return; }
        setReadyToRender(false);
        const id = requestAnimationFrame(() => setReadyToRender(true));
        return () => cancelAnimationFrame(id);
    }, [popoverBreadcrumb]);

    const { node, entries, childEntries, nodeData } = useMemo(() => {
        if (!popoverBreadcrumb || !readyToRender) return { node: null, entries: [], childEntries: [], nodeData: null };
        if (popoverBreadcrumb === 't') {
            return { node: null, entries: [], childEntries: [], nodeData: topLevelNodeData || null };
        }
        const n = findNodeByBreadcrumb(objectMap, popoverBreadcrumb);
        const e = (entriesByBreadcrumb[popoverBreadcrumb] || []).filter(x => x.amount !== 0 || x.description || x.notes);
        const c = collectChildEntries(popoverBreadcrumb, entriesByBreadcrumb).filter(x => x.amount !== 0 || x.description || x.notes);
        return { node: n, entries: e, childEntries: c, nodeData: n?.data || null };
    }, [popoverBreadcrumb, readyToRender, objectMap, entriesByBreadcrumb, topLevelNodeData]);

    if (!popoverBreadcrumb || !pivotPopoverProps) return null;

    // Instant backdrop + spinner while EtcPopover is mounting on next frame
    if (!readyToRender) {
        return (
            <div
                onClick={() => setPopoverBreadcrumb(null)}
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15, 23, 42, 0.18)' }}
            >
                <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.4)', borderTop: '3px solid #ffffff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (popoverBreadcrumb !== 't' && !node) return null;

    return (
        <EtcPopover
            entries={entries}
            childEntries={childEntries}
            breadcrumb={popoverBreadcrumb}
            nodeData={nodeData}
            drillColumns={pivotPopoverProps.drillColumns}
            tabConfig={pivotPopoverProps.tabConfig}
            selectedJob={pivotPopoverProps.selectedJob}
            selectedVersion={pivotPopoverProps.selectedVersion}
            lockedVersion={pivotPopoverProps.lockedVersion}
            userData={pivotPopoverProps.userData}
            setUserData={pivotPopoverProps.setUserData}
            lookupOptions={pivotPopoverProps.lookupOptions}
            onClose={() => setPopoverBreadcrumb(null)}
            onSaved={() => setPopoverBreadcrumb(null)}
            onDeleted={() => {}}
        />
    );
};

export default EtcPopoverHost;
